import { useEffect } from "react";
import { FiX, FiDownload } from "react-icons/fi";

export function isVideoMedia(url, label = "") {
  const s = `${url || ""} ${label || ""}`.toLowerCase();
  return /\.(mp4|mov|m4v|webm|avi|mkv)(\?|$)/i.test(s) || /\/video\//i.test(s);
}

export function isImageMedia(url, label = "", mime = "") {
  if (String(mime || "").startsWith("image/")) return true;
  const s = `${url || ""} ${label || ""}`.toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)(\?|$)/i.test(s);
}

/**
 * Fullscreen image / video player for support chat media.
 * @param {{ url: string, kind: 'image'|'video', title?: string, onClose: () => void }} props
 */
export default function ChatMediaLightbox({ url, kind, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={kind === "video" ? "Video player" : "Image preview"}
    >
      <div
        className="absolute top-3 right-3 flex items-center gap-2 z-[101]"
        onClick={(e) => e.stopPropagation()}
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium"
          title="Download"
        >
          <FiDownload className="w-3.5 h-3.5" />
          Download
        </a>
        <button
          type="button"
          onClick={onClose}
          className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Close"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div
        className="relative max-w-[96vw] max-h-[90vh] w-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <p className="text-white/80 text-xs mb-2 truncate max-w-full px-2">{title}</p>
        ) : null}
        {kind === "video" ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-h-[85vh] max-w-full rounded-xl bg-black shadow-2xl"
          />
        ) : (
          <img
            src={url}
            alt={title || "Preview"}
            className="max-h-[85vh] max-w-full object-contain rounded-xl shadow-2xl"
          />
        )}
      </div>
    </div>
  );
}
